import { Button } from "@fefyi/ui/button";
import { motion, useInView, useScroll, useTransform } from "motion/react";
import { useRef, useState } from "react";

const variants = {
  hidden: {
    opacity: 0,
    rotateY: 12,
  },
  visible: {
    opacity: 1,
    rotateY: 0,
  },
};

export const BentoGrid = () => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { amount: 0.3 });
  const [roomToScroll, setRoomToScroll] = useState(false);

  return (
    <div className="flex w-full flex-col items-center">
      <p className="mb-3 text-md">Need more room to scroll? 👇</p>
      <Button
        onClick={() => setRoomToScroll((r) => !r)}
        size="small"
        className="mb-6"
      >
        Click me please!
      </Button>
      {roomToScroll && <div className="h-[50vh] w-full" />}
      <motion.div
        variants={{
          hidden: {
            transition: {
              staggerChildren: 0.1,
              staggerDirection: -1,
            },
          },
          visible: {
            transition: {
              staggerChildren: 0.1,
            },
          },
        }}
        animate={inView ? "visible" : "hidden"}
        initial="hidden"
        ref={ref}
        className="relative grid w-full grid-cols-2 grid-rows-2 gap-4 [perspective:700px]"
      >
        <motion.div
          variants={variants}
          className="row-span-2 rounded-2xl bg-background-contrast"
        ></motion.div>
        <motion.div
          variants={variants}
          className="aspect-2/1 rounded-2xl bg-background-contrast"
        ></motion.div>
        <motion.div
          variants={variants}
          className="aspect-2/1 rounded-2xl bg-background-contrast"
        ></motion.div>
      </motion.div>
    </div>
  );
};

export const ProductsBento = () => {
  const cardOneRef = useRef<HTMLDivElement>(null);
  const cardTwoRef = useRef<HTMLDivElement>(null);
  const cardThreeRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress: opacityOne } = useScroll({
    target: cardOneRef,
    offset: ["center end", "end end"],
  });
  const { scrollYProgress: opacityTwo } = useScroll({
    target: cardTwoRef,
    offset: ["center end", "end end"],
  });
  const { scrollYProgress: opacityThree } = useScroll({
    target: cardThreeRef,
    offset: ["center end", "end end"],
  });

  const scaleOne = useTransform(opacityOne, [0, 1], [0.95, 1]);
  const scaleTwo = useTransform(opacityTwo, [0, 1], [0.95, 1]);
  const scaleThree = useTransform(opacityThree, [0, 1], [0.95, 1]);

  return (
    <>
      <div className="flex w-full flex-col items-center px-8 text-black">
        <div className="relative grid w-full max-w-[800px] grid-cols-1 gap-4 sm:h-[600px] sm:grid-cols-2 sm:grid-rows-2">
          <motion.div
            style={{ opacity: opacityOne, scale: scaleOne }}
            ref={cardOneRef}
            className="row-span-2"
          >
            <Card
              title="TryPhone 29"
              subtitle="Call me maybe"
              imageUrl="https://sandpack.frontend.fyi/img/courses/framer-motion/product-1.webp"
            />
          </motion.div>

          <motion.div
            style={{ opacity: opacityTwo, scale: scaleTwo }}
            ref={cardTwoRef}
          >
            <Card
              title="BearShots Pro"
              subtitle="Ever heard better?"
              imageUrl="https://sandpack.frontend.fyi/img/courses/framer-motion/product-2.webp"
            />
          </motion.div>
          <motion.div
            style={{ opacity: opacityThree, scale: scaleThree }}
            ref={cardThreeRef}
          >
            <Card
              title="Trample Watch"
              subtitle="Right on time"
              imageUrl="https://sandpack.frontend.fyi/img/courses/framer-motion/product-3.webp"
            />
          </motion.div>
        </div>
      </div>
    </>
  );
};

type CardProps = {
  title: string;
  subtitle: string;
  imageUrl: string;
};

const Card = ({ title, subtitle, imageUrl }: CardProps) => (
  <div className="h-full overflow-clip rounded-2xl bg-white">
    <div className="mb-3 p-4 text-center">
      <h2 className="mb-0 text-2xl leading-snug">{title}</h2>
      <p>{subtitle}</p>
    </div>
    <img className="h-full w-full object-cover" src={imageUrl} alt="" />
  </div>
);
